import { useAuth } from '@context';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Navigation } from '@components/Navigation';
import { NavigationItemList } from '@components/Navigation/types';
import { WingmanPanel } from '@components/WingmanPanel';
import {
  useNovuNotifications,
  useNovuConfig,
  useNotificationPreferences,
  UserRoleService,
} from '@services';
import { useSetAtom, useAtomValue } from 'jotai';
import { NOVU_BACKEND_URL, NOVU_SOCKET_URL } from '@constants';
import { AppShell } from '@views/AppShell/AppShell';
import {
  clientNavigation,
  managerNavigation,
  dashboardNavigation,
} from './navigationItems';
import { isPublicPageAtom } from '../../store/jotai';
import { showAiFeaturesAtom } from '../../store/flags';

export function RequireAuth() {
  const setIsPublicPage = useSetAtom(isPublicPageAtom);
  const showAiFeatures = useAtomValue(showAiFeaturesAtom);
  const { isWarehouseClient } = UserRoleService();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  // Fetch Novu configuration from backend
  const { config: novuConfig, loading: configLoading } = useNovuConfig();

  // Initialize Novu notifications for authenticated users
  const notificationToken = localStorage.getItem('notificationToken');

  useNovuNotifications({
    applicationIdentifier: novuConfig?.applicationIdentifier,
    subscriberId: user?.id || '',
    subscriberHash: notificationToken || undefined,
    backendUrl: NOVU_BACKEND_URL,
    socketUrl: NOVU_SOCKET_URL,
    isInitialisingNovu:
      isAuthenticated &&
      !!user?.id &&
      !!notificationToken &&
      !configLoading &&
      !!novuConfig?.applicationIdentifier,
  });

  // Hydrates the shared `notificationPreferencesAtom` once on auth so the inbox category-tab
  // filter and digest UI both read from the same server source.
  useNotificationPreferences();

  const buildNavigationItems = (): NavigationItemList => {
    const items = isWarehouseClient(user)
      ? clientNavigation
      : managerNavigation;
    return showAiFeatures ? [dashboardNavigation, ...items] : items;
  };

  setIsPublicPage(false);

  return isAuthenticated ? (
    <AppShell>
      <Navigation items={buildNavigationItems()}>
        <Outlet />
      </Navigation>
      {showAiFeatures && <WingmanPanel />}
    </AppShell>
  ) : (
    <Navigate to='/login' state={{ from: location }} replace />
  );
}
